import { useEffect, useState, useRef } from "react";
import { api, formatApiError, BACKEND_ORIGIN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Settings as SettingsIcon, Loader2, Plus, X, Save, Image as ImageIcon, KeyRound, Share2, Megaphone, Users, Palette, Search as SearchIcon, LayoutGrid, Trash2, Upload, Star } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND = BACKEND_ORIGIN || "";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

function loadGoogleFont(family) {
  if (!family) return;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function CategoryImageUpload({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5 MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = resolveUrl(value);

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-lg border border-border bg-white flex items-center justify-center overflow-hidden shrink-0">
          {preview ? (
            <img src={preview} alt="Category" className="max-h-full max-w-full object-cover" />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="cat-image-file" />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cat-image-upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {uploading ? "Uploading..." : "Upload image"}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} data-testid="cat-image-clear-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" data-testid="cat-image-url" className="text-xs" />
        </div>
      </div>
    </div>
  );
}

function MediaLibraryTab() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/uploads?limit=200")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded (${res.backend === "object_storage" ? "cloud" : "local"})`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyUrl = async (url) => {
    const full = `${window.location.origin}${url}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const remove = async (filename) => {
    if (!window.confirm("Delete this file? (Soft delete — cannot be undone from admin panel)")) return;
    try {
      await api.delete(`/admin/uploads/${filename}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="media-library">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl">Media Library</h3>
          <p className="text-sm text-muted-foreground">
            {data.total} file{data.total === 1 ? "" : "s"} stored ·
            <span className="ml-1 text-teal font-semibold">Powered by Emergent Object Storage</span> — persists across deployments.
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="media-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-teal hover:bg-teal-dark text-white" data-testid="media-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Uploading..." : "Upload file"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border-2 border-dashed border-border rounded-xl">
          No files yet. Upload your first image to get started.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {data.items.map((f) => (
            <div key={f.id} className="group border border-border rounded-xl overflow-hidden bg-white" data-testid={`media-item-${f.filename}`}>
              <div className="aspect-square bg-secondary/30 flex items-center justify-center overflow-hidden">
                <img src={resolveUrl(f.url)} alt={f.original_filename} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
              <div className="p-2 text-xs space-y-1">
                <div className="truncate font-medium" title={f.original_filename}>{f.original_filename}</div>
                <div className="text-muted-foreground flex items-center justify-between">
                  <span>{(f.size / 1024).toFixed(1)} KB</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${f.backend === "object_storage" ? "bg-teal/10 text-teal" : "bg-amber-500/10 text-amber-700"}`}>
                    {f.backend === "object_storage" ? "cloud" : "local"}
                  </span>
                </div>
                <div className="flex gap-1 pt-1">
                  <Button size="sm" variant="ghost" onClick={() => copyUrl(f.url)} className="flex-1 h-7 text-xs" data-testid={`media-copy-${f.filename}`}>Copy URL</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(f.filename)} className="h-7 text-destructive" data-testid={`media-delete-${f.filename}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const empty = { name: "", slug: "", icon: "Compass", image: "", description: "", link_query: "", product_category: "", booking_type: "per_person", parent_id: "", display_order: 99, is_active: true };
  const save = async () => {
    try {
      if (editing.id) await api.put(`/categories/${editing.id}`, editing);
      else await api.post("/categories", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Deleted"); load();
  };
  // Group categories: parents first, then children indented
  const parentCats = cats.filter((c) => !c.parent_id);
  const childCats = cats.filter((c) => c.parent_id);
  const orderedCats = [];
  parentCats.forEach((p) => {
    orderedCats.push(p);
    childCats.filter((c) => c.parent_id === p.id).forEach((c) => orderedCats.push(c));
  });
  // Orphaned children (parent doesn't exist)
  childCats.filter((c) => !parentCats.find((p) => p.id === c.parent_id)).forEach((c) => orderedCats.push(c));
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Icon uses <a href="https://lucide.dev/icons" target="_blank" rel="noreferrer" className="text-teal underline">any Lucide name</a> (Plane, Hotel, Ticket, Home, Mountain, Globe...)</p>
        <Button onClick={() => setEditing(empty)} variant="outline" data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {orderedCats.map((c) => (
        <div key={c.id} className={`border border-border rounded-lg p-3 flex items-center gap-3 ${c.parent_id ? "ml-8 border-dashed" : ""}`}>
          {c.image && <img src={resolveUrl(c.image)} alt="" className="w-12 h-12 rounded object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              {c.parent_id && <span className="text-xs text-muted-foreground mr-1">↳</span>}
              {c.name} <span className="text-xs text-muted-foreground">/ {c.slug}</span>
              {c.product_category && <span className="text-xs bg-teal/10 text-teal px-1.5 py-0.5 rounded ml-2">{c.product_category}</span>}
              {c.booking_type && c.booking_type !== "per_person" && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded ml-1">{c.booking_type}</span>}
            </div>
            <div className="text-xs text-muted-foreground truncate">{c.description}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-cat-${c.slug}`}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-destructive" data-testid={`del-cat-${c.slug}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {editing && (
        <div className="border border-teal rounded-lg p-4 space-y-3 bg-secondary/20">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="cat-name" /></div>
            <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="cat-slug" /></div>
            <div><Label>Lucide icon</Label><Input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} placeholder="Plane" /></div>
            <div><Label>Order</Label><Input type="number" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} /></div>
            <div>
              <Label>Product Category Value</Label>
              <Input value={editing.product_category || ""} onChange={(e) => setEditing({ ...editing, product_category: e.target.value })} placeholder="e.g. tour, cab, hotel, ticket" data-testid="cat-product-category" />
              <span className="text-xs text-muted-foreground">Value stored on products (e.g. tour, cab, hotel)</span>
            </div>
            <div>
              <Label>Booking Type</Label>
              <select
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                value={editing.booking_type || "per_person"}
                onChange={(e) => setEditing({ ...editing, booking_type: e.target.value })}
                data-testid="cat-booking-type"
              >
                <option value="per_person">Per Person</option>
                <option value="per_vehicle">Per Vehicle</option>
                <option value="per_room">Per Room / Night</option>
              </select>
            </div>
            <div>
              <Label>Parent Category</Label>
              <select
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                value={editing.parent_id || ""}
                onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })}
                data-testid="cat-parent"
              >
                <option value="">— Top Level (No Parent) —</option>
                {parentCats.filter((p) => p.id !== editing.id).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-1">
              <Label>Category image</Label>
              <CategoryImageUpload value={editing.image || ""} onChange={(v) => setEditing({ ...editing, image: v })} />
            </div>
            <div className="sm:col-span-2"><Label>Description</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-saffron text-white hover:bg-saffron-dark" data-testid="cat-save">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BrandingTab({ data, setField }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const bodyFont = data.brand_font_body || "Inter";
  const displayFont = data.brand_font_display || "Playfair Display";

  useEffect(() => {
    loadGoogleFont(bodyFont);
    loadGoogleFont(displayFont);
  }, [bodyFont, displayFont]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("logo_url", res.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const logoPreview = resolveUrl(data.logo_url);

  return (
    <Card className="p-6 space-y-4">
      <h3 className="font-display text-xl">Site Identity, Logo, Colors & Fonts</h3>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Site Name</Label>
          <Input value={data.site_name || ""} onChange={(e) => setField("site_name", e.target.value)} placeholder="HyderabadTourism.in" data-testid="site-name" />
          <p className="text-xs text-muted-foreground mt-1">Appears in header, footer, SEO tags, payment screens & emails.</p>
        </div>
        <div>
          <Label>Site URL (canonical domain)</Label>
          <Input value={data.site_url || ""} onChange={(e) => setField("site_url", e.target.value)} placeholder="https://hyderabadtourism.in" data-testid="site-url" />
          <p className="text-xs text-muted-foreground mt-1">Used for SEO canonical links & structured data. Leave blank to auto-detect.</p>
        </div>
      </div>

      <div className="space-y-3 border border-border rounded-xl p-4">
        <Label>Logo</Label>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-24 h-24 rounded-xl border border-border bg-white flex items-center justify-center overflow-hidden shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
            ) : (
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 w-full space-y-2">
            <div className="flex gap-2 flex-wrap">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? "Uploading..." : "Upload logo"}
              </Button>
              {data.logo_url && (
                <Button type="button" variant="ghost" onClick={() => setField("logo_url", "")} data-testid="logo-clear-btn">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
            <Input value={data.logo_url || ""} onChange={(e) => setField("logo_url", e.target.value)} placeholder="or paste image URL" data-testid="logo-url" />
            <p className="text-xs text-muted-foreground">PNG / JPG / SVG · max 5 MB · transparent background recommended.</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Primary color (buttons, headers)</Label>
          <div className="flex gap-2">
            <Input value={data.brand_primary || "#007085"} onChange={(e) => setField("brand_primary", e.target.value)} data-testid="brand-primary" />
            <input type="color" value={data.brand_primary || "#007085"} onChange={(e) => setField("brand_primary", e.target.value)} className="w-10 h-10 rounded border border-border" />
          </div>
        </div>
        <div>
          <Label>Accent color (badges, highlights)</Label>
          <div className="flex gap-2">
            <Input value={data.brand_accent || "#00AAD6"} onChange={(e) => setField("brand_accent", e.target.value)} data-testid="brand-accent" />
            <input type="color" value={data.brand_accent || "#00AAD6"} onChange={(e) => setField("brand_accent", e.target.value)} className="w-10 h-10 rounded border border-border" />
          </div>
        </div>
        <div>
          <Label>Display font (headings)</Label>
          <Input value={displayFont} onChange={(e) => setField("brand_font_display", e.target.value)} placeholder="Playfair Display" data-testid="brand-font-display" />
          <p className="text-xs text-muted-foreground mt-1">Use any <a href="https://fonts.google.com" target="_blank" rel="noreferrer" className="text-teal underline">Google Fonts</a> family name.</p>
        </div>
        <div>
          <Label>Body font</Label>
          <Input value={bodyFont} onChange={(e) => setField("brand_font_body", e.target.value)} placeholder="Inter" data-testid="brand-font-body" />
        </div>
      </div>

      {/* Live font preview */}
      <div className="border border-teal rounded-xl p-5 bg-secondary/20" data-testid="font-preview">
        <div className="text-xs uppercase tracking-widest text-teal mb-2">Live preview</div>
        <div className="text-3xl mb-2" style={{ fontFamily: `'${displayFont}', Georgia, serif` }}>
          Discover Hyderabad. Explore beyond.
        </div>
        <div className="text-sm" style={{ fontFamily: `'${bodyFont}', system-ui, sans-serif` }}>
          Book tours, hotels, tickets, cabs and adventures — all in one place. GST-compliant invoices. Razorpay secured payments.
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Note: Colors apply to logo & CTA overlays. Save to persist. Full theme repaint may need a reload.</p>
    </Card>
  );
}

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-12 text-center text-muted-foreground">Loading settings...</div>;

  const setField = (k, v) => setData({ ...data, [k]: v });
  const setSocial = (k, v) => setData({ ...data, social_links: { ...(data.social_links || {}), [k]: v } });
  const setBadge = (k, v) => setData({ ...data, trust_badges: { ...(data.trust_badges || {}), [k]: v } });

  const updateTestimonial = (i, k, v) => {
    const list = [...(data.testimonials || [])];
    list[i] = { ...list[i], [k]: v };
    setData({ ...data, testimonials: list });
  };

  const addTestimonial = () => {
    setData({
      ...data,
      testimonials: [...(data.testimonials || []), { name: "", role: "", text: "", rating: 5 }],
    });
  };

  const removeTestimonial = (i) => {
    setData({ ...data, testimonials: data.testimonials.filter((_, j) => j !== i) });
  };


function DataManagementTab() {
  const [purging, setPurging] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [targets, setTargets] = useState({ bookings: true, enquiries: true, customers: true, vendors: false, loyalty: true, reviews: true });
  const [result, setResult] = useState(null);

  const toggleTarget = (key) => setTargets((t) => ({ ...t, [key]: !t[key] }));

  const handlePurge = async () => {
    if (confirmText !== "PURGE") return toast.error("Type PURGE to confirm");
    setPurging(true);
    try {
      const activeTargets = Object.entries(targets).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/admin/purge-test-data", { confirm: "PURGE_ALL_TEST_DATA", targets: activeTargets });
      setResult(data.purged);
      toast.success("Test data purged successfully");
      setConfirmText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const TARGET_LABELS = {
    bookings: { label: "Bookings", desc: "All test bookings and payment records" },
    enquiries: { label: "Enquiries", desc: "All customer enquiry submissions" },
    customers: { label: "Customer Accounts", desc: "All non-admin user accounts" },
    vendors: { label: "Vendor Accounts", desc: "All vendor accounts and profiles" },
    loyalty: { label: "Loyalty Transactions", desc: "Point history and reset balances to 0" },
    reviews: { label: "Reviews", desc: "All product reviews and ratings" },
  };

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="font-display text-xl text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Purge Test Data</h3>
        <p className="text-sm text-muted-foreground mt-1">Use this before going live to clear all test bookings, enquiries, and customer data. <strong>Products, categories, and admin accounts are never deleted.</strong></p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(TARGET_LABELS).map(([key, { label, desc }]) => (
          <label key={key} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${targets[key] ? "border-red-300 bg-red-50" : "border-border"}`}>
            <Checkbox checked={targets[key]} onCheckedChange={() => toggleTarget(key)} data-testid={`purge-${key}`} />
            <div>
              <span className="font-medium text-sm">{label}</span>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
          </label>
        ))}
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="purge-result">
          <h4 className="font-semibold text-green-800 mb-2">Purge Complete</h4>
          <ul className="text-sm text-green-700 space-y-1">
            {Object.entries(result).map(([k, v]) => <li key={k}>{k.replace(/_/g, " ")}: <strong>{typeof v === "boolean" ? "Done" : v}</strong></li>)}
          </ul>
        </div>
      )}

      <div className="border-t pt-4 space-y-3">
        <p className="text-sm font-medium text-red-600">Type <code className="bg-red-100 px-1.5 py-0.5 rounded">PURGE</code> to confirm</p>
        <div className="flex gap-3 items-end">
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type PURGE here"
            className="max-w-xs"
            data-testid="purge-confirm-input"
          />
          <Button
            onClick={handlePurge}
            disabled={purging || confirmText !== "PURGE"}
            variant="destructive"
            className="shrink-0"
            data-testid="purge-submit"
          >
            {purging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Purge Selected Data
          </Button>
        </div>
      </div>
    </Card>
  );
}

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal text-white flex items-center justify-center">
            <SettingsIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl">Site Settings</h1>
            <p className="text-muted-foreground text-sm">Content, branding, integrations</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
        </Button>
      </div>

      <Tabs defaultValue="hero">
        <TabsList className="w-full justify-start bg-white border border-border flex-wrap h-auto">
          <TabsTrigger value="hero" data-testid="tab-settings-hero"><ImageIcon className="w-4 h-4 mr-1.5" />Hero & CTA</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-settings-contact"><Megaphone className="w-4 h-4 mr-1.5" />Contact & Trust</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-settings-social"><Share2 className="w-4 h-4 mr-1.5" />Social Media</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="tab-settings-testimonials"><Users className="w-4 h-4 mr-1.5" />Testimonials</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-settings-categories"><LayoutGrid className="w-4 h-4 mr-1.5" />Categories</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-settings-branding"><Palette className="w-4 h-4 mr-1.5" />Branding</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-settings-seo"><SearchIcon className="w-4 h-4 mr-1.5" />SEO / Ages</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-settings-integrations"><KeyRound className="w-4 h-4 mr-1.5" />Integrations</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="tab-settings-loyalty"><Star className="w-4 h-4 mr-1.5" />Loyalty Program</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-settings-media"><ImageIcon className="w-4 h-4 mr-1.5" />Media Library</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-settings-data"><Trash2 className="w-4 h-4 mr-1.5" />Data Management</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="mt-4 space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Homepage Hero</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Badge text</Label>
                <Input value={data.hero_badge || ""} onChange={(e) => setField("hero_badge", e.target.value)} data-testid="hero-badge" />
              </div>
              <div>
                <Label>Background image URL</Label>
                <Input value={data.hero_image || ""} onChange={(e) => setField("hero_image", e.target.value)} data-testid="hero-image" />
              </div>
              <div>
                <Label>Headline (line 1)</Label>
                <Input value={data.hero_title_line1 || ""} onChange={(e) => setField("hero_title_line1", e.target.value)} data-testid="hero-line1" />
              </div>
              <div>
                <Label>Headline (line 2, saffron)</Label>
                <Input value={data.hero_title_line2 || ""} onChange={(e) => setField("hero_title_line2", e.target.value)} data-testid="hero-line2" />
              </div>
              <div className="sm:col-span-2">
                <Label>Subtitle</Label>
                <Textarea rows={3} value={data.hero_subtitle || ""} onChange={(e) => setField("hero_subtitle", e.target.value)} data-testid="hero-subtitle" />
              </div>
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Search Ticker Suggestions</h3>
            <p className="text-sm text-muted-foreground">These appear as animated placeholder text in the hero search box. One suggestion per line.</p>
            <Textarea
              rows={8}
              value={(data.search_ticker_suggestions || []).join("\n")}
              onChange={(e) => setField("search_ticker_suggestions", e.target.value.split("\n").filter((s) => s.trim()))}
              placeholder="Hyderabad City Tour&#10;Ramoji Film City Tickets&#10;Charminar Heritage Walk"
              data-testid="search-ticker-suggestions"
            />
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Bottom CTA Section</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA Title</Label>
                <Input value={data.cta_title || ""} onChange={(e) => setField("cta_title", e.target.value)} data-testid="cta-title" />
              </div>
              <div>
                <Label>CTA Subtitle</Label>
                <Textarea value={data.cta_subtitle || ""} onChange={(e) => setField("cta_subtitle", e.target.value)} rows={2} data-testid="cta-subtitle" />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-4 space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Contact details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={data.contact_phone || ""} onChange={(e) => setField("contact_phone", e.target.value)} data-testid="contact-phone" /></div>
              <div><Label>Email</Label><Input value={data.contact_email || ""} onChange={(e) => setField("contact_email", e.target.value)} data-testid="contact-email" /></div>
              <div><Label>WhatsApp number (digits only with country code)</Label><Input value={data.whatsapp_number || ""} onChange={(e) => setField("whatsapp_number", e.target.value)} placeholder="919000000000" data-testid="whatsapp-number" /></div>
              <div><Label>Address</Label><Input value={data.contact_address || ""} onChange={(e) => setField("contact_address", e.target.value)} data-testid="contact-address" /></div>
              <div><Label>GSTIN</Label><Input value={data.gstin || ""} onChange={(e) => setField("gstin", e.target.value)} data-testid="gstin" /></div>
              <div><Label>IATA / License</Label><Input value={data.iata || ""} onChange={(e) => setField("iata", e.target.value)} /></div>
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Trust badges</h3>
            <p className="text-sm text-muted-foreground">Customize the 4 trust badges shown on the homepage below the hero section.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Established year</Label><Input value={data.trust_badges?.est_year || ""} onChange={(e) => setBadge("est_year", e.target.value)} /></div>
              <div><Label>Happy customers count</Label><Input value={data.trust_badges?.happy_customers || ""} onChange={(e) => setBadge("happy_customers", e.target.value)} placeholder="55,000+" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 border-t pt-4">
              <div><Label>Badge 1 Title</Label><Input value={data.trust_badges?.badge1_title || ""} onChange={(e) => setBadge("badge1_title", e.target.value)} placeholder="Est. 2005" /></div>
              <div><Label>Badge 1 Subtitle</Label><Input value={data.trust_badges?.badge1_sub || ""} onChange={(e) => setBadge("badge1_sub", e.target.value)} placeholder="20+ years of expertise" /></div>
              <div><Label>Badge 2 Title</Label><Input value={data.trust_badges?.badge2_title || ""} onChange={(e) => setBadge("badge2_title", e.target.value)} placeholder="GST Compliant" /></div>
              <div><Label>Badge 2 Subtitle</Label><Input value={data.trust_badges?.badge2_sub || ""} onChange={(e) => setBadge("badge2_sub", e.target.value)} placeholder="Proper tax invoices" /></div>
              <div><Label>Badge 3 Title</Label><Input value={data.trust_badges?.badge3_title || ""} onChange={(e) => setBadge("badge3_title", e.target.value)} placeholder="100% Secure" /></div>
              <div><Label>Badge 3 Subtitle</Label><Input value={data.trust_badges?.badge3_sub || ""} onChange={(e) => setBadge("badge3_sub", e.target.value)} placeholder="Razorpay protected" /></div>
              <div><Label>Badge 4 Title</Label><Input value={data.trust_badges?.badge4_title || ""} onChange={(e) => setBadge("badge4_title", e.target.value)} placeholder="55,000+ Travellers" /></div>
              <div><Label>Badge 4 Subtitle</Label><Input value={data.trust_badges?.badge4_sub || ""} onChange={(e) => setBadge("badge4_sub", e.target.value)} placeholder="Happy customers" /></div>
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Online Booking</h3>
            <p className="text-sm text-muted-foreground">When disabled globally, all products default to "Enquire Only" mode. You can also enable booking per-product from the product editor.</p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={data.online_booking_enabled || false} onChange={(e) => setField("online_booking_enabled", e.target.checked)} className="w-4 h-4 rounded" data-testid="online-booking-toggle" />
                <span className="text-sm font-medium">Enable online booking globally</span>
              </label>
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Partner / Licence Logos</h3>
            <p className="text-sm text-muted-foreground">Add authorized partner logos (TGTDC, APTDC, RFC etc.) to display in the footer. Upload images via Media Library first.</p>
            <div className="space-y-3">
              {(data.partner_logos || []).map((logo, i) => (
                <div key={i} className="flex items-center gap-3 border border-border rounded-lg p-3">
                  {logo.image_url && <img src={resolveUrl(logo.image_url)} alt="" className="w-16 h-10 object-contain" />}
                  <div className="flex-1 grid sm:grid-cols-3 gap-2">
                    <Input placeholder="Name (e.g. TGTDC)" value={logo.name || ""} onChange={(e) => { const l = [...(data.partner_logos || [])]; l[i] = { ...l[i], name: e.target.value }; setField("partner_logos", l); }} />
                    <Input placeholder="Image URL" value={logo.image_url || ""} onChange={(e) => { const l = [...(data.partner_logos || [])]; l[i] = { ...l[i], image_url: e.target.value }; setField("partner_logos", l); }} />
                    <Input placeholder="Link URL (optional)" value={logo.link || ""} onChange={(e) => { const l = [...(data.partner_logos || [])]; l[i] = { ...l[i], link: e.target.value }; setField("partner_logos", l); }} />
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { const l = [...(data.partner_logos || [])]; l.splice(i, 1); setField("partner_logos", l); }}><X className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setField("partner_logos", [...(data.partner_logos || []), { name: "", image_url: "", link: "" }])} data-testid="add-partner-logo"><Plus className="w-4 h-4 mr-1" />Add Logo</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="mt-4">
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Social media links</h3>
            <p className="text-sm text-muted-foreground">Paste full URLs. Leave blank to hide.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {["facebook", "instagram", "youtube", "twitter", "linkedin"].map((p) => (
                <div key={p}>
                  <Label className="capitalize">{p}</Label>
                  <Input value={data.social_links?.[p] || ""} onChange={(e) => setSocial(p, e.target.value)} placeholder={`https://${p}.com/your-handle`} data-testid={`social-${p}`} />
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="testimonials" className="mt-4 space-y-4">
          <Card className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl">Testimonials</h3>
              <Button variant="outline" onClick={addTestimonial} data-testid="add-testimonial"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-4">
              {(data.testimonials || []).map((t, i) => (
                <div key={i} className="border border-border rounded-xl p-4 space-y-3 relative">
                  <button onClick={() => removeTestimonial(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-destructive" data-testid={`remove-testimonial-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><Label>Name</Label><Input value={t.name} onChange={(e) => updateTestimonial(i, "name", e.target.value)} /></div>
                    <div><Label>Role / Location</Label><Input value={t.role} onChange={(e) => updateTestimonial(i, "role", e.target.value)} /></div>
                    <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} value={t.rating} onChange={(e) => updateTestimonial(i, "rating", Number(e.target.value))} /></div>
                  </div>
                  <div><Label>Quote</Label><Textarea rows={2} value={t.text} onChange={(e) => updateTestimonial(i, "text", e.target.value)} /></div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <Card className="p-6">
            <h3 className="font-display text-xl mb-4">Homepage Categories</h3>
            <CategoryManager />
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="mt-4 space-y-4">
          <BrandingTab data={data} setField={setField} />
        </TabsContent>

        <TabsContent value="seo" className="mt-4 space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">SEO / Meta tags</h3>
            <div><Label>Meta title</Label><Input value={data.seo_meta_title || ""} onChange={(e) => setField("seo_meta_title", e.target.value)} data-testid="seo-title" /></div>
            <div><Label>Meta description</Label><Textarea rows={2} value={data.seo_meta_description || ""} onChange={(e) => setField("seo_meta_description", e.target.value)} data-testid="seo-desc" /></div>
            <div><Label>Meta keywords</Label><Textarea rows={2} value={data.seo_meta_keywords || ""} onChange={(e) => setField("seo_meta_keywords", e.target.value)} /></div>
            <div><Label>Open Graph image URL</Label><Input value={data.seo_og_image || ""} onChange={(e) => setField("seo_og_image", e.target.value)} /></div>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Footer SEO Links</h3>
            <p className="text-sm text-muted-foreground">Add custom links that appear in the footer for internal linking and SEO value. Great for popular tours, destination pages, and key landing pages.</p>
            {(data.footer_seo_links || []).map((link, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1"><Input value={link.label || ""} onChange={(e) => { const arr = [...(data.footer_seo_links || [])]; arr[i] = { ...arr[i], label: e.target.value }; setField("footer_seo_links", arr); }} placeholder="Hyderabad City Tour" /></div>
                <div className="flex-1"><Input value={link.url || ""} onChange={(e) => { const arr = [...(data.footer_seo_links || [])]; arr[i] = { ...arr[i], url: e.target.value }; setField("footer_seo_links", arr); }} placeholder="/hyderabad-city-tour" /></div>
                <Button variant="ghost" size="icon" className="shrink-0 text-destructive" onClick={() => { const arr = (data.footer_seo_links || []).filter((_, j) => j !== i); setField("footer_seo_links", arr); }} data-testid={`remove-seo-link-${i}`}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setField("footer_seo_links", [...(data.footer_seo_links || []), { label: "", url: "" }])} data-testid="add-seo-link">+ Add SEO Link</Button>
          </Card>
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Passenger age policies</h3>
            <div className="grid sm:grid-cols-4 gap-3">
              <div><Label>Adult min age</Label><Input type="number" value={data.adult_min_age || 12} onChange={(e) => setField("adult_min_age", Number(e.target.value))} data-testid="adult-min-age" /></div>
              <div><Label>Child min age</Label><Input type="number" value={data.child_min_age || 4} onChange={(e) => setField("child_min_age", Number(e.target.value))} data-testid="child-min-age" /></div>
              <div><Label>Child max age</Label><Input type="number" value={data.child_max_age || 11} onChange={(e) => setField("child_max_age", Number(e.target.value))} data-testid="child-max-age" /></div>
              <div><Label>Infant max age</Label><Input type="number" value={data.infant_max_age || 3} onChange={(e) => setField("infant_max_age", Number(e.target.value))} /></div>
            </div>
            <p className="text-xs text-muted-foreground">These are surfaced on the pax steppers across the site.</p>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4 space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Google Analytics 4</h3>
            <p className="text-sm text-muted-foreground">Paste your GA4 Measurement ID from <a href="https://analytics.google.com/analytics/web/#/a/streams" target="_blank" rel="noreferrer" className="text-teal underline">Google Analytics → Admin → Data Streams → Web</a>. Format: <code className="bg-secondary px-1 py-0.5 rounded">G-XXXXXXXXXX</code>. Live changes propagate on next page load.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Measurement ID</Label>
                <Input value={data.ga_measurement_id || ""} onChange={(e) => setField("ga_measurement_id", e.target.value.trim())} placeholder="G-XXXXXXXXXX" data-testid="ga-measurement-id" />
              </div>
              <div className="flex items-end">
                <div className="text-xs text-muted-foreground">
                  Status: {data.ga_measurement_id ? <span className="text-teal font-semibold">Tracking active ({data.ga_measurement_id})</span> : <span className="text-amber-600 font-semibold">Not configured</span>}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Razorpay Payment Gateway</h3>
            <p className="text-sm text-muted-foreground">Get keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="text-teal underline">Razorpay Dashboard → Settings → API Keys</a>. Empty keys ⇒ Mock mode.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Key ID</Label><Input value={data.razorpay_key_id || ""} onChange={(e) => setField("razorpay_key_id", e.target.value)} placeholder="rzp_test_xxx" data-testid="rzp-key-id" /></div>
              <div><Label>Key Secret</Label><Input type="password" value={data.razorpay_key_secret || ""} onChange={(e) => setField("razorpay_key_secret", e.target.value)} placeholder="********" data-testid="rzp-key-secret" /></div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Interakt WhatsApp Business</h3>
            <p className="text-sm text-muted-foreground">From <a href="https://app.interakt.ai/" target="_blank" rel="noreferrer" className="text-teal underline">Interakt → Developer Settings</a>. Template must be pre-approved.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>API Key (base64)</Label><Input type="password" value={data.interakt_api_key || ""} onChange={(e) => setField("interakt_api_key", e.target.value)} placeholder="********" data-testid="interakt-key" /></div>
              <div className="sm:col-span-2"><Label>Booking confirmation template name</Label><Input value={data.interakt_template_booking_confirm || ""} onChange={(e) => setField("interakt_template_booking_confirm", e.target.value)} placeholder="booking_confirmation" /></div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Zoho Bigin CRM</h3>
            <p className="text-sm text-muted-foreground">Zoho Bigin is configured via server environment variables. Bookings and enquiries are automatically pushed to your Bigin pipeline.</p>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2.5 h-2.5 rounded-full ${data._keys_configured?.zoho_bigin ? "bg-green-500" : "bg-red-400"}`} />
              <span>{data._keys_configured?.zoho_bigin ? "Connected & Active" : "Not configured — add ZOHO_CLIENT_ID, ZOHO_CLIENT_SECRET, ZOHO_REFRESH_TOKEN to .env"}</span>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-display text-xl">Email Notifications (SMTP)</h3>
            <p className="text-sm text-muted-foreground">Configure Google Workspace SMTP to receive booking, enquiry and cancellation notifications. Generate an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-teal underline">App Password</a> from your Google account.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>SMTP Host</Label><Input value={data.smtp_host || "smtp.gmail.com"} onChange={(e) => setField("smtp_host", e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
              <div><Label>SMTP Port</Label><Input type="number" value={data.smtp_port || 587} onChange={(e) => setField("smtp_port", Number(e.target.value))} placeholder="587" data-testid="smtp-port" /></div>
              <div><Label>Sender Email</Label><Input value={data.smtp_email || ""} onChange={(e) => setField("smtp_email", e.target.value)} placeholder="info@hyderabadtourism.in" data-testid="smtp-email" /></div>
              <div><Label>App Password</Label><Input type="password" value={data.smtp_password || ""} onChange={(e) => setField("smtp_password", e.target.value)} placeholder="xxxx xxxx xxxx xxxx" data-testid="smtp-password" /></div>
              <div className="sm:col-span-2"><Label>Notification Recipient Email</Label><Input value={data.notification_email || ""} onChange={(e) => setField("notification_email", e.target.value)} placeholder="info@yourdomain.com" data-testid="notification-email" /></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="loyalty" className="mt-4 space-y-4">
          <Card className="p-6 space-y-5">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Star className="w-5 h-5 text-saffron" /> Loyalty Points Program</h3>
            <p className="text-sm text-muted-foreground">Control how customers earn and redeem loyalty points. Points are only awarded on products where loyalty is explicitly enabled.</p>

            <div className="grid sm:grid-cols-2 gap-6">
              {/* Global earning toggle */}
              <div className="border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Enable Earning</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Master switch for earning points on bookings</p>
                  </div>
                  <Switch checked={!!data.loyalty_earning_enabled} onCheckedChange={(v) => setField("loyalty_earning_enabled", v)} data-testid="loyalty-earning-toggle" />
                </div>
                {data.loyalty_earning_enabled && (
                  <div>
                    <Label>Default earn rate (points per ₹100)</Label>
                    <Input type="number" min={0} value={data.loyalty_points_per_100 ?? 10} onChange={(e) => setField("loyalty_points_per_100", Number(e.target.value) || 0)} data-testid="loyalty-earn-rate" />
                    <p className="text-xs text-muted-foreground mt-1">Products must individually enable loyalty. This is the fallback rate when a product doesn't set its own.</p>
                  </div>
                )}
              </div>

              {/* Global redemption toggle */}
              <div className="border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Enable Redemption</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Allow customers to use points for discounts at checkout</p>
                  </div>
                  <Switch checked={!!data.loyalty_redemption_enabled} onCheckedChange={(v) => setField("loyalty_redemption_enabled", v)} data-testid="loyalty-redeem-toggle" />
                </div>
                {data.loyalty_redemption_enabled && (
                  <div className="space-y-3">
                    <div>
                      <Label>Point value (₹ per point)</Label>
                      <Input type="number" min={0} step={0.1} value={data.loyalty_point_value ?? 1} onChange={(e) => setField("loyalty_point_value", Number(e.target.value) || 0)} data-testid="loyalty-point-value" />
                    </div>
                    <div>
                      <Label>Minimum points to redeem</Label>
                      <Input type="number" min={1} value={data.loyalty_min_redeem ?? 50} onChange={(e) => setField("loyalty_min_redeem", Number(e.target.value) || 1)} data-testid="loyalty-min-redeem" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground">
              <strong>How it works:</strong> Even with earning enabled globally, points are only awarded for products that have "Enable loyalty points" turned ON in the product editor. You can also set a custom earn rate per product.
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="media" className="mt-4 space-y-4">
          <MediaLibraryTab />
        </TabsContent>

        <TabsContent value="data" className="mt-4 space-y-4">
          <DataManagementTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
